// Cliente del motor de comisiones (`retail-commission-business`), tipado con `RetailCommission`.
// Mockups: surekeep/mockups/backoffice/portal-admin-vl-comisiones.html y portal-admin-vl-com-pagadas.html
import type { RetailCommission } from '@fiado/type-kit';
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_RETAIL_COMMISSION_BASE_URL;
const enc = encodeURIComponent;

export type RuleAction = 'publish' | 'pause' | 'resume' | 'expire';

/** Lo que el corte no paga y declara, por ejemplo add-ons del modelo viejo. */
export interface PayoutExclusion {
    code: string;
    label: string;
    reason: string;
}

/** El CSV que arma el motor: el nombre del archivo y su contenido. */
export interface EngineCsvExport {
    filename: string;
    csv: string;
}

export type PayoutsPage = RetailCommission.ListPayoutsResponse & { exclusions: PayoutExclusion[] };
export type PayoutView = RetailCommission.GetPayoutResponse & { exclusions: PayoutExclusion[] };

/** El devengo de una venta tal como lo guarda el motor. */
export type SaleAccrual = RetailCommission.CommissionAccrual & {
    saleInput?: RetailCommission.CommissionSaleInput;
};

function query(params: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') search.set(key, String(value));
    }
    const text = search.toString();
    return text ? `?${text}` : '';
}

const get = <T>(path: string) => apiFetch<T>(path, { baseUrl: BASE_URL });

function send<T>(
    method: 'POST' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
) {
    return apiFetch<T>(path, {
        method,
        baseUrl: BASE_URL,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...(headers ? { headers } : {}),
    });
}

export const commissionEngine = {
    // ── Plan, reglas y políticas ──
    getRuleSet: () => get<RetailCommission.GetRuleSetResponse>('/backoffice/commissions/plan'),
    listRules: () => get<RetailCommission.ListRulesResponse>('/backoffice/commissions/rules'),
    createRule: (body: RetailCommission.CreateRuleRequest) =>
        send<RetailCommission.RuleResponse>('POST', '/backoffice/commissions/rules', body),
    updateRule: (ruleId: string, body: RetailCommission.UpdateRuleRequest) =>
        send<RetailCommission.RuleResponse>(
            'PUT',
            `/backoffice/commissions/rules/${enc(ruleId)}`,
            body,
        ),
    ruleAction: (ruleId: string, action: RuleAction, expectedVersion: number) =>
        send<RetailCommission.RuleActionResponse>(
            'POST',
            `/backoffice/commissions/rules/${enc(ruleId)}/${action}`,
            { expectedVersion },
        ),
    deleteRule: (ruleId: string) =>
        send<{ ruleId: string }>('DELETE', `/backoffice/commissions/rules/${enc(ruleId)}`),
    ruleHistory: (ruleId: string) =>
        get<RetailCommission.RuleHistoryResponse>(
            `/backoffice/commissions/rules/${enc(ruleId)}/history`,
        ),
    simulateRules: (body: RetailCommission.SimulateRulesRequest) =>
        send<RetailCommission.SimulateRulesResponse>(
            'POST',
            '/backoffice/commissions/rules/simulate',
            body,
        ),
    getPolicies: () =>
        get<RetailCommission.GetPoliciesResponse>('/backoffice/commissions/policies'),
    updatePolicies: (body: RetailCommission.UpdatePoliciesRequest) =>
        send<RetailCommission.UpdatePoliciesResponse>(
            'PUT',
            '/backoffice/commissions/policies',
            body,
        ),

    // ── Corte vivo ──
    getLivePayout: (periodId?: string) =>
        get<RetailCommission.GetLivePayoutResponse>(
            `/backoffice/commissions/live${query({ periodId })}`,
        ),
    getLiveSeller: (sellerId: string, periodId?: string) =>
        get<RetailCommission.GetLiveSellerResponse>(
            `/backoffice/commissions/live/sellers/${enc(sellerId)}${query({ periodId })}`,
        ),

    // ── Cortes cerrados ──
    listPayouts: (limit?: number, cursor?: string) =>
        get<PayoutsPage>(`/backoffice/commissions/payouts${query({ limit, cursor })}`),
    getPayout: (periodId: string) =>
        get<PayoutView>(`/backoffice/commissions/payouts/${enc(periodId)}`),
    getPayoutSeller: (periodId: string, sellerId: string) =>
        get<RetailCommission.GetPayoutSellerResponse>(
            `/backoffice/commissions/payouts/${enc(periodId)}/sellers/${enc(sellerId)}`,
        ),
    getAggregates: (
        by:
            | RetailCommission.CommissionAggregateByEnum
            | `${RetailCommission.CommissionAggregateByEnum}`,
        from: string,
        to: string,
    ) =>
        get<RetailCommission.GetPayoutAggregatesResponse>(
            `/backoffice/commissions/payouts/aggregates${query({ by, from, to })}`,
        ),
    getSeries: (
        by: RetailCommission.CommissionSeriesByEnum | `${RetailCommission.CommissionSeriesByEnum}`,
        id: string,
        weeks?: number,
    ) =>
        get<RetailCommission.GetPayoutSeriesResponse>(
            `/backoffice/commissions/payouts/series${query({ by, id, weeks })}`,
        ),
    // El ajuste es dinero sin venta detrás: el reintento tiene que llevar la misma llave.
    createAdjustment: (
        periodId: string,
        body: RetailCommission.CreateAdjustmentRequest,
        idempotencyKey: string,
    ) =>
        send<RetailCommission.CreateAdjustmentResponse>(
            'POST',
            `/backoffice/commissions/payouts/${enc(periodId)}/adjustments`,
            body,
            { 'Idempotency-Key': idempotencyKey },
        ),
    exportPayout: (periodId: string) =>
        get<EngineCsvExport>(`/backoffice/commissions/payouts/export${query({ periodId })}`),

    // ── Devengo de una venta ──
    getAccrualBySale: (saleId: string, sellerId: string, soldAt: string) =>
        get<{ accrual: SaleAccrual }>(
            `/backoffice/commissions/accruals/by-sale/${enc(saleId)}${query({ sellerId, soldAt })}`,
        ),

    // ── Pagos a la wallet ──
    getPaymentAttention: () =>
        get<RetailCommission.GetPaymentAttentionResponse>(
            '/backoffice/commissions/payments/attention',
        ),
    getSellerLedger: (sellerId: string) =>
        get<RetailCommission.GetSellerLedgerResponse>(
            `/backoffice/commissions/sellers/${enc(sellerId)}/ledger`,
        ),
    retrySellerPayment: (sellerId: string) =>
        send<RetailCommission.RetrySellerPaymentView>(
            'POST',
            `/backoffice/commissions/payments/sellers/${enc(sellerId)}/retry`,
        ),
    // Dinero que sale del pendiente sin depósito: el reintento tiene que llevar la misma llave.
    markPaidOutside: (
        sellerId: string,
        body: RetailCommission.MarkPaidOutsideRequest,
        idempotencyKey: string,
    ) =>
        send<RetailCommission.MarkPaidOutsideView>(
            'POST',
            `/backoffice/commissions/payments/sellers/${enc(sellerId)}/paid-outside`,
            body,
            { 'Idempotency-Key': idempotencyKey },
        ),
    resolvePayment: (paymentId: string, body: RetailCommission.ResolvePaymentRequest) =>
        send<RetailCommission.ResolvePaymentView>(
            'POST',
            `/backoffice/commissions/payments/${enc(paymentId)}/resolve`,
            body,
        ),

    // ── Estados de cuenta ──
    // La mesa central manda la cadena; al Admin VL el motor le impone la del token.
    listStatements: (retailerId?: string) =>
        get<RetailCommission.ListStatementsResponse>(
            `/backoffice/commissions/statements${query({ retailerId })}`,
        ),
    getStatement: (statementId: string, retailerId?: string) =>
        get<RetailCommission.GetStatementResponse>(
            `/backoffice/commissions/statements/${enc(statementId)}${query({ retailerId })}`,
        ),
    exportStatement: (statementId: string, retailerId?: string) =>
        get<EngineCsvExport>(
            `/backoffice/commissions/statements/${enc(statementId)}/export${query({ retailerId })}`,
        ),

    // ── Portal Vendedor ──
    /** Mis comisiones: el vendedor sale del token, no se manda. */
    getMine: (weeks?: number) =>
        get<RetailCommission.GetMyCommissionsResponse>(`/commissions/me${query({ weeks })}`),
    /** Mis pagos: el saldo y los depósitos a la wallet del vendedor del token. */
    getMyPayments: () => get<RetailCommission.GetMyPaymentsResponse>('/commissions/me/payments'),
};
